import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { useAuth } from '@shared/auth/AuthContext'
import { useActivityBasedRefresh } from '@shared/auth/useActivityBasedRefresh'
import { Box, CircularProgress } from '@mui/material'
import ErrorBoundary from './components/ErrorBoundary'
import { TrialExpiredModal } from './components/TrialExpiredModal'
import { MfaSetupRequiredModal } from './components/MfaSetupRequiredModal'
import LoginPage from './pages/LoginPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import DashboardLayout from './layouts/DashboardLayout'
import MarketingLayout from './layouts/MarketingLayout'
import HomePage from './pages/marketing/HomePage'
import FAQPage from './pages/marketing/FAQPage'
import FeaturesPage from './pages/marketing/FeaturesPage'
import SignupPage from './pages/marketing/SignupPage'
import EmailVerificationPage from './pages/marketing/EmailVerificationPage'
import CustomerDashboard from './pages/customer/Dashboard'
import CustomerTickets from './pages/customer/Tickets'
import CustomerTicketDetail from './pages/customer/TicketDetail'
import CustomerCreateTicket from './pages/customer/CreateTicket'
import CustomerProfile from './pages/customer/Profile'
import CustomerAnalytics from './pages/customer/Analytics'
import AgentDashboard from './pages/agent/Dashboard'
import AgentQueue from './pages/agent/Queue'
import AgentTickets from './pages/agent/Tickets'
import AgentTicketDetail from './pages/agent/TicketDetail'
import AgentCreateTicket from './pages/agent/CreateTicket'
import AgentAnalytics from './pages/agent/Analytics'
import AdminDashboard from './pages/admin/Dashboard'
import AdminUsers from './pages/admin/Users'
import AdminOrganizations from './pages/admin/OrganizationsNew'
import AdminOrganizationDetail from './pages/admin/OrganizationDetail'
import AdminChildSiteDetail from './pages/admin/ChildSiteDetail'
import AdminBilling from './pages/admin/Billing'
import AdminSettings from './pages/admin/Settings'
import AdminTickets from './pages/admin/Tickets'
import AdminAnalytics from './pages/admin/Analytics'
import AdminActivity from './pages/admin/Activity'
import AdminHelpGuide from './pages/admin/HelpGuide'
import AdminEscalationPaths from './pages/admin/EscalationPaths'
import AdminSlaEscalationAnalytics from './pages/admin/SlaEscalationAnalytics'
import AdminEmailTemplates from './pages/admin/EmailTemplates'
import TeamsIntegrationGuide from './pages/docs/TeamsIntegrationGuide'
import ZapierIntegrationGuide from './pages/docs/ZapierIntegrationGuide'
import CustomerHowToGuide from './pages/customer/HowToGuide'
import TermsPage from './pages/TermsPage'
import PrivacyPage from './pages/PrivacyPage'
import UpgradePage from './pages/billing/UpgradePage'
import BillingSuccessPage from './pages/billing/BillingSuccessPage'
import AcceptInvitationPage from './pages/AcceptInvitationPage'
import MicrosoftCallbackPage from './pages/MicrosoftCallbackPage'
import MarketplaceLanding from './pages/marketplace/MarketplaceLanding'
import MarketplaceSetup from './pages/marketplace/MarketplaceSetup'
import NotFoundPage from './pages/NotFoundPage'
import ServerErrorPage from './pages/ServerErrorPage'
import MaintenancePage from './pages/MaintenancePage'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}

function RoleBasedRedirect() {
  const { user } = useAuth()

  if (!user) return <Navigate to="/login" replace />

  // Redirect to appropriate dashboard based on role
  if (user.role === 'Admin') {
    return <Navigate to="/admin/dashboard" replace />
  } else if (user.role === 'Agent') {
    return <Navigate to="/agent/dashboard" replace />
  } else {
    // Customer and Oversight users use customer portal with different visibility
    return <Navigate to="/customer/dashboard" replace />
  }
}

function RoleGuard({ children, allowedRoles }: { children: React.ReactNode, allowedRoles: string[] }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!user || !allowedRoles.includes(user.role)) {
    // Redirect unauthorized users to their appropriate dashboard
    return <Navigate to="/admin/dashboard" replace />
  }

  return <>{children}</>
}

function LandingPage() {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isAuthenticated) {
    return <RoleBasedRedirect />
  }

  return (
    <MarketingLayout>
      <HomePage />
    </MarketingLayout>
  )
}

function AppContent() {
  const { requiresMfaSetup } = useAuth()

  // Enable activity-based token refresh (30-min inactivity timeout, 8-hour absolute max)
  useActivityBasedRefresh()

  return (
    <>
      <TrialExpiredModal />
      <MfaSetupRequiredModal open={requiresMfaSetup} />
      <Routes>
        {/* Root route - landing page for unauthenticated, dashboard redirect for authenticated */}
        <Route path="/" element={<LandingPage />} />

        {/* Login page */}
        <Route path="/login" element={<LoginPage />} />

        {/* Password reset pages */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Signup page */}
        <Route path="/signup" element={<SignupPage />} />

        {/* Email verification page - using /welcome/activate for less suspicious URL */}
        <Route path="/welcome/activate" element={<EmailVerificationPage />} />
        {/* Keep old route for backwards compatibility with emails already sent */}
        <Route path="/verify-email" element={<EmailVerificationPage />} />

        {/* Accept invitation page */}
        <Route path="/auth/accept-invitation" element={<AcceptInvitationPage />} />

        {/* Microsoft SSO redirect callback */}
        <Route path="/auth/callback" element={<MicrosoftCallbackPage />} />

        {/* Microsoft Marketplace routes */}
        <Route path="/marketplace/landing" element={<MarketplaceLanding />} />
        <Route path="/marketplace/setup" element={<MarketplaceSetup />} />

        {/* Terms and Privacy pages */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        {/* Features page */}
        <Route path="/features" element={<FeaturesPage />} />

        {/* FAQ page */}
        <Route path="/faq" element={<FAQPage />} />

        {/* Billing routes (accessible to all authenticated users) */}
        <Route path="/billing/upgrade" element={
          <ProtectedRoute>
            <UpgradePage />
          </ProtectedRoute>
        } />
        <Route path="/billing/success" element={
          <ProtectedRoute>
            <BillingSuccessPage />
          </ProtectedRoute>
        } />

        {/* Customer routes */}
        <Route path="/customer/*" element={
          <ProtectedRoute>
            <DashboardLayout userType="customer">
              <Routes>
                <Route path="dashboard" element={<CustomerDashboard />} />
                <Route path="tickets" element={<CustomerTickets />} />
                <Route path="tickets/:id" element={<CustomerTicketDetail />} />
                <Route path="create-ticket" element={<CustomerCreateTicket />} />
                <Route path="analytics" element={<CustomerAnalytics />} />
                <Route path="profile" element={<CustomerProfile />} />
                <Route path="help" element={<CustomerHowToGuide />} />
                <Route path="*" element={<Navigate to="/customer/dashboard" replace />} />
              </Routes>
            </DashboardLayout>
          </ProtectedRoute>
        } />

        {/* Agent routes */}
        <Route path="/agent/*" element={
          <ProtectedRoute>
            <DashboardLayout userType="agent">
              <Routes>
                <Route path="dashboard" element={<AgentDashboard />} />
                <Route path="queue" element={<AgentQueue />} />
                <Route path="tickets" element={<AgentTickets />} />
                <Route path="tickets/:id" element={<AgentTicketDetail />} />
                <Route path="create-ticket" element={<AgentCreateTicket />} />
                <Route path="analytics" element={<AgentAnalytics />} />
                <Route path="help" element={<AdminHelpGuide />} />
                <Route path="*" element={<Navigate to="/agent/dashboard" replace />} />
              </Routes>
            </DashboardLayout>
          </ProtectedRoute>
        } />

        {/* Admin routes */}
        <Route path="/admin/*" element={
          <ProtectedRoute>
            <DashboardLayout userType="admin">
              <Routes>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="tickets" element={<AdminTickets />} />
                <Route path="activity" element={<AdminActivity />} />
                <Route path="users" element={
                  <RoleGuard allowedRoles={['Admin']}>
                    <AdminUsers />
                  </RoleGuard>
                } />
                <Route path="organizations" element={
                  <RoleGuard allowedRoles={['Admin']}>
                    <AdminOrganizations />
                  </RoleGuard>
                } />
                <Route path="organizations/:id" element={
                  <RoleGuard allowedRoles={['Admin']}>
                    <AdminOrganizationDetail />
                  </RoleGuard>
                } />
                <Route path="childsites/:id" element={
                  <RoleGuard allowedRoles={['Admin']}>
                    <AdminChildSiteDetail />
                  </RoleGuard>
                } />
                <Route path="billing" element={
                  <RoleGuard allowedRoles={['Admin']}>
                    <AdminBilling />
                  </RoleGuard>
                } />
                <Route path="settings" element={
                  <RoleGuard allowedRoles={['Admin']}>
                    <AdminSettings />
                  </RoleGuard>
                } />
                <Route path="email-templates" element={
                  <RoleGuard allowedRoles={['Admin']}>
                    <AdminEmailTemplates />
                  </RoleGuard>
                } />
                <Route path="escalation-paths" element={
                  <RoleGuard allowedRoles={['Admin']}>
                    <AdminEscalationPaths />
                  </RoleGuard>
                } />
                <Route path="sla-escalation-analytics" element={
                  <RoleGuard allowedRoles={['Admin', 'Oversight']}>
                    <AdminSlaEscalationAnalytics />
                  </RoleGuard>
                } />
                <Route path="analytics" element={
                  <RoleGuard allowedRoles={['Admin']}>
                    <AdminAnalytics />
                  </RoleGuard>
                } />
                <Route path="docs/teams-integration" element={
                  <RoleGuard allowedRoles={['Admin']}>
                    <TeamsIntegrationGuide />
                  </RoleGuard>
                } />
                <Route path="docs/zapier-integration" element={
                  <RoleGuard allowedRoles={['Admin']}>
                    <ZapierIntegrationGuide />
                  </RoleGuard>
                } />
                <Route path="help" element={
                  <RoleGuard allowedRoles={['Admin']}>
                    <AdminHelpGuide />
                  </RoleGuard>
                } />
                <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
              </Routes>
            </DashboardLayout>
          </ProtectedRoute>
        } />

        {/* Error pages */}
        <Route path="/500" element={<ServerErrorPage />} />
        <Route path="/503" element={<MaintenancePage />} />

        {/* Catch-all 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  )
}

export default App